'use client';

import { useMemo, useState } from 'react';
import {
  CURATED_PAIRS,
  generatePairs,
  pairToCss,
  type FontCategory,
  type FontPair,
} from '@/lib/browser-colortext-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const CATEGORY_OPTIONS: { value: FontCategory; label: string }[] = [
  { value: 'serif', label: 'Serif' },
  { value: 'sans', label: 'Sans-serif' },
  { value: 'mono', label: 'Monospace' },
  { value: 'display', label: 'Display' },
];

export default function BrowserFontPairGenerator() {
  const [mode, setMode] = useState<'curated' | 'generate'>('curated');
  const [headingCategory, setHeadingCategory] = useState<FontCategory>('sans');
  const [bodyCategory, setBodyCategory] = useState<FontCategory>('serif');
  const [sampleHeading, setSampleHeading] = useState('The quick brown fox');
  const [sampleBody, setSampleBody] = useState(
    'Pack my box with five dozen liquor jugs. A wizard’s job is to vex chumps quickly in fog. How vexingly quick daft zebras jump.',
  );
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const pairs = useMemo<FontPair[]>(
    () => (mode === 'curated' ? CURATED_PAIRS : generatePairs(headingCategory, bodyCategory)),
    [mode, headingCategory, bodyCategory],
  );

  async function handleCopy(pair: FontPair) {
    try {
      await copyText(pairToCss(pair));
      setCopyMessage(`Copied CSS for ${pair.heading.name} + ${pair.body.name}.`);
    } catch {
      setCopyMessage('The browser could not copy the CSS.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Typography</span>
        <h2 className="tool-live-title">Font pairing generator</h2>
        <p className="tool-live-subtitle">
          Preview heading and body font combinations with web-safe fallbacks, then copy the CSS
          font-family stacks.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Pairing source</span>
              <select
                onChange={(event) => setMode(event.target.value as 'curated' | 'generate')}
                value={mode}
              >
                <option value="curated">Curated picks</option>
                <option value="generate">Generate by category</option>
              </select>
            </label>
            {mode === 'generate' ? (
              <>
                <label className="tool-live-field">
                  <span>Heading style</span>
                  <select
                    onChange={(event) => setHeadingCategory(event.target.value as FontCategory)}
                    value={headingCategory}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="tool-live-field">
                  <span>Body style</span>
                  <select
                    onChange={(event) => setBodyCategory(event.target.value as FontCategory)}
                    value={bodyCategory}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}
            <label className="tool-live-field tool-live-field-full">
              <span>Sample heading</span>
              <input
                onChange={(event) => setSampleHeading(event.target.value)}
                type="text"
                value={sampleHeading}
              />
            </label>
            <label className="tool-live-field tool-live-field-full">
              <span>Sample paragraph</span>
              <textarea
                onChange={(event) => setSampleBody(event.target.value)}
                rows={4}
                value={sampleBody}
              />
            </label>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
          <p className="tool-live-panel-note">
            {pairs.length} pairing{pairs.length === 1 ? '' : 's'} shown. Everything renders locally
            with system and web-safe fonts.
          </p>
        </section>

        <section className="tool-live-preview">
          <div style={{ display: 'grid', gap: '1rem' }}>
            {pairs.map((pair) => (
              <article
                key={`${pair.heading.name}-${pair.body.name}`}
                style={{
                  border: '1px solid rgba(148, 163, 184, 0.35)',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                }}
              >
                <p
                  style={{
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    opacity: 0.7,
                    margin: '0 0 0.5rem',
                  }}
                >
                  {pair.heading.name} + {pair.body.name} — {pair.note}
                </p>
                <p
                  style={{
                    fontFamily: pair.heading.stack,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    margin: '0 0 0.5rem',
                  }}
                >
                  {sampleHeading || 'The quick brown fox'}
                </p>
                <p
                  style={{
                    fontFamily: pair.body.stack,
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    margin: '0 0 0.75rem',
                  }}
                >
                  {sampleBody || 'Pack my box with five dozen liquor jugs.'}
                </p>
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    opacity: 0.85,
                    display: 'grid',
                    gap: '0.15rem',
                    marginBottom: '0.75rem',
                    wordBreak: 'break-word',
                  }}
                >
                  <span>heading: {pair.heading.stack}</span>
                  <span>body: {pair.body.stack}</span>
                </div>
                <div className="tool-live-actions">
                  <button
                    className="button button-secondary"
                    onClick={() => void handleCopy(pair)}
                    type="button"
                  >
                    Copy CSS
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
